import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { getCurrentUserIdSafe } from "../utils/auth";
import { workoutService } from "../services/workout";

interface BackfillWorkoutModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  initialDate: Date;
  setDate: (date: Date) => void;
}

export function BackfillWorkoutModal({ open, onClose, onSaved, initialDate, setDate }: BackfillWorkoutModalProps) {
  const [activity, setActivity] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Use the lifted date state
  const date = initialDate;

  const handleSave = async () => {
    setSaving(true);
    try {
      const userId = await getCurrentUserIdSafe();
      if (!userId) throw new Error("User not authenticated");
      const workout = {
        id: `backfill-${Date.now()}`,
        name: activity,
        description: notes || null,
        exercises: [],
        user_id: userId,
        status: "completed",
        exercise_results: {},
      };
      // Insert the log with the selected date and duration
      const logId = await workoutService.createWorkoutLog(workout, userId);
      // Patch the log to set status, date, and duration
      await workoutService.overrideWorkoutLog(logId, {
        created_at: date.toISOString(),
        started_at: date.toISOString(),
        completed_at: date.toISOString(),
      });
      setSaving(false);
      onSaved();
      onClose();
    } catch (err) {
      setSaving(false);
      alert("Failed to save backfilled workout: " + (err as Error).message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Backfill One-Off Workout</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <Calendar selected={date} onDayClick={(d: Date | undefined) => d && setDate(d)} className="w-full" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Activity</label>
            <Input
              type="text"
              value={activity}
              onChange={(e) => setActivity(e.target.value)}
              placeholder="e.g. Hike, Yoga, Soccer"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Anything else about this activity..."
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || !activity || !date}>
            {saving ? "Saving..." : "Save Workout"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
